import { DateTime } from 'luxon';

import { Package } from '@/common/models';
import PackageProperty from '@/common/models/package/PackageProperty';
import Status from '@/common/models/package/PackageStatus.enum';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import API from '@/org-app/api';
import * as lineitemTypes from '@/org-app/store/modules/lineItems/mutation-types';
import rootStore from '@/store';
import { createApiAction, createGetAllAction, createGetOneAction } from '@/utils/ActionsUtils';
import UserNotifications from '@/utils/UserNotifications';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  createNewPackage({ commit }, pack = Package.createNew()) {
    commit(types.CREATE_PACKAGE, pack);
    return Promise.resolve(pack);
  },

  async createNewPackageFromPlan({ commit }, { plan }) {
    const obj = Package.createNew();
    obj.name = plan.name;
    obj.budgetMode = plan.budgetMode;
    obj.adDurationMin = plan.adDuration;
    obj.mediaOwnerIDs = plan.mediaOwnerIDs;
    obj.minSecondsBetweenPlaysMin = plan.minSecondsBetweenPlays;
    obj.budgetMin = plan.budget;
    obj.marketTargetedImpressionsMin = plan.targetImpressions;

    // CPM
    let cpm = null;
    if (!plan.isCPMFromRateCard) {
      if (plan.cpmCalculator.computedCPM != null && plan.cpmCalculator.computedCPM.length > 0) {
        [cpm] = plan.cpmCalculator.computedCPM;
      } else {
        cpm = plan.cpmCalculator.cpmFixed;
      }
    }
    obj.averageCPM = cpm;
    obj.cpmFixed = cpm;
    obj.cpmNet = plan.isCPMFromRateCard ? null : plan.cpmNet;

    // DATE
    obj.startDate = plan.startDate < DateTime.local() ? DateTime.local() : DateTime.fromISO(plan.startDate);
    const maxPossibleEndDate = obj.startDate.plus({ days: 7 });
    const planEndDate = plan.endDate < DateTime.local() ? DateTime.local().plus({ days: 7 }) : plan.endDate;
    obj.endDate = DateTime.min(DateTime.fromISO(planEndDate), maxPossibleEndDate);

    // TARGETING
    obj.networkIDs = plan.networkIDs;
    obj.locationTargetIDs = plan.locationIDs;
    obj.locationExclusionIDs = plan.locationExclusionIDs;
    obj.siteIDs = plan.siteIDs;
    obj.siteExclusionIDs = plan.siteExclusionIDs;
    obj.unitIDs = plan.unitIDs;
    obj.unitExclusionIDs = plan.unitExclusionIDs;
    obj.marketIDs = plan.marketIDs;
    obj.geofenceGroupIDs = plan.geofenceGroupIDs;
    obj.unitTags = plan.unitTags;
    obj.unitTagExclusions = plan.unitTagExclusions;
    obj.creativeFormats = plan.creativeFormats;
    obj.creativeFormatExclusions = plan.creativeFormatExclusions;
    obj.unitIDs = plan.unitIDs;
    obj.unitExclusionIDs = plan.unitExclusionIDs;
    obj.mediaTypeIDs = plan.mediaTypeIDs;
    obj.unitPackExclusionIDs = plan.unitPackExclusionIDs;
    obj.unitPackIDs = plan.unitPackIDs;
    obj.demographicTargetIDs = plan.demographicIDs;
    obj.daypart = plan.daypart;
    obj.iabCategories = plan.iabCategories;
    obj.iabCategoryExclusions = plan.iabCategoryExclusions;
    obj.creativeOrientations = plan.creativeOrientations;
    obj.creativeOrientationExclusions = plan.creativeOrientationExclusions;
    obj.languageIDs = plan.languageIDs;
    obj.languageExclusionIDs = plan.languageExclusionIDs;

    obj.isCPMFromRateCard = plan.isCPMFromRateCard;
    obj.organizationID = plan.organizationID;
    obj.packageProperties = plan.planProperties?.map((x) => new PackageProperty(null, null, x.key, x.value));

    const pack = Package.fromObject(obj);
    commit(types.CREATE_PACKAGE, pack);
    return pack;
  },

  deletePackages({ commit }, packages) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;

      const evalCompletion = () => {
        if (successfullApiCalls + failedApiCalls === packages.length) {
          if (successfullApiCalls === packages.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject();
            const msg = i18n.global.t('messages.failedToDeletePackages');
            UserNotifications.error({ message: `${msg}` });
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      packages.forEach((pack) => {
        API.instance.deletePackage(
          pack.id,
          () => {
            commit(types.DELETE_PACKAGE, pack);
            successfullApiCalls += 1;
            evalCompletion();
          },
          () => {
            failedApiCalls += 1;
            evalCompletion();
          },
        );
      });
    });
  },

  deletePackage({ commit }, pack) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.deletePackage(
        pack.id,
        () => {
          commit(types.DELETE_PACKAGE, pack);
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToDeletePackage');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getPackages: createGetAllAction(
    API.instance.getPackages,
    types.UPDATE_PACKAGES_LIST,
    'messages.failedToGetPackages',
    true,
  ),

  getCuratorPackage: createGetOneAction(
    API.instance.getCuratorPackage,
    types.UPDATE_PACKAGES_LIST,
    'messages.failedToGetPackage',
    true,
  ),

  getCuratorPackages: createGetAllAction(
    API.instance.getCuratorPackages,
    types.UPDATE_PACKAGES_LIST,
    'messages.failedToGetPackages',
    true,
  ),

  getPackage({ commit }, { id, isProgrammaticGuaranteed = false, loadBuyerDSPs = false }) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);
      API.instance.getPackage(
        id,
        isProgrammaticGuaranteed,
        async (pack) => {
          commit(types.UPDATE_PACKAGE, pack);
          if (loadBuyerDSPs) {
            await rootStore.dispatch('orgApp/getPackageBuyerDSPs', pack);
          }
          resolve(pack);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetPackage');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getPackagesMatchingTargeting({ commit }, { payload }) {
    return new Promise((resolve, reject) => {
      API.instance.getPackagesMatchingTargeting(
        payload,
        (response) => {
          resolve(response);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetPackagesMatchingTargeting');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  async getPackageBuyerDSPs({ commit }, pack) {
    if (pack.buyerOrganizationDSPIDs?.length > 0) {
      try {
        const isSupplyOrganization = rootStore.getters['orgApp/isSupplyOrganization'];
        const organizationDSPs = isSupplyOrganization ? await rootStore.dispatch('orgApp/getOrganizationDSPs') : [];
        const packageBuyerOrganizationDSPs = organizationDSPs.items.filter((x) =>
          pack.buyerOrganizationDSPIDs.includes(x.id),
        );
        rootStore.dispatch('updatePackageBuyerOrganizationDSPs', {
          package: pack,
          value: packageBuyerOrganizationDSPs,
        });
      } catch (error) {
        const msg = i18n.global.t('messages.failedToGetOrganizationDSPBuyers');
        ErrorHandler.showError(msg, error);
      }
    } else {
      rootStore.dispatch('updatePackageBuyerOrganizationDSPs', {
        pack,
        value: [],
      });
    }
  },

  getPackageCreatives({ commit }, { id }) {
    return new Promise((resolve, reject) => {
      API.instance.getPackageCreatives(
        id,
        (packageCreatives) => {
          resolve(packageCreatives);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetPackage');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getPackageAudit({ commit }, id) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.getPackageAudit(
        id,
        (pack) => {
          resolve(pack);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetPackageAudit');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getPackageDetails({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getPackageDetails(
        params,
        (stats) => {
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetPackageDetailsStats');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getCuratedPackageDetails({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getCuratedPackageDetails(
        params,
        (stats) => {
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetCuratedPackageDetailsStats');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getCuratedPackageSteeringSummary({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getCuratedPackageSteeringSummary(
        params,
        (stats) => {
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToGetCuratedPackageDetailsStats');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getPackageSummaryStats({ commit }, params) {
    return new Promise((resolve, reject) => {
      API.instance.getPackageSummaryStats(
        params,
        (stats) => {
          resolve(stats);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedTogetPackageSummaryStats');
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  getPackageDefaultRTBMetrics({ commit }, { packageId, start, end, keepSeries = false, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      API.instance.getPackageDefaultRTBMetrics(
        packageId,
        { start, end, keep_series: keepSeries },
        (response) => {
          resolve(response);
          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
        (error) => {
          const msg = 'FAIL';
          ErrorHandler.showError(msg, error);
          reject(error);
          if (showLoader) {
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        },
      );
    });
  },

  getPackageBiddingMetrics({ commit }, { packageIDs, start = null, end = null }) {
    return new Promise((resolve, reject) => {
      API.instance.getPackageBiddingMetrics(
        { packageIDs, start, end },
        (response) => {
          resolve(response);
        },
        (error) => {
          const msg = 'FAIL';
          ErrorHandler.showError(msg, error);
          reject(error);
        },
      );
    });
  },

  publishPackages({ commit }, packages) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;
      const evalCompletion = () => {
        if (successfullApiCalls + failedApiCalls === packages.length) {
          if (successfullApiCalls === packages.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject();
            const msg = i18n.global.t('messages.failedToPublishPackages');
            UserNotifications.error({ message: `${msg}` });
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      packages.forEach((pack) => {
        API.instance.putPackageState(
          pack,
          () => {
            successfullApiCalls += 1;
            evalCompletion();
          },
          () => {
            failedApiCalls += 1;
            evalCompletion();
          },
        );
      });
    });
  },

  unpublishPackages({ commit }, packages) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;
      const evalCompletion = () => {
        if (successfullApiCalls + failedApiCalls === packages.length) {
          if (successfullApiCalls === packages.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject();
            const msg = i18n.global.t('messages.failedToPublishPackages');
            UserNotifications.error({ message: `${msg}` });
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      packages.forEach((pack) => {
        pack.state = Status.Draft;
        API.instance.putPackageState(
          pack,
          () => {
            successfullApiCalls += 1;
            evalCompletion();
          },
          () => {
            failedApiCalls += 1;
            evalCompletion();
          },
        );
      });
    });
  },

  retirePackages({ commit }, packages) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;
      const evalCompletion = () => {
        if (successfullApiCalls + failedApiCalls === packages.length) {
          if (successfullApiCalls === packages.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject();
            const msg = i18n.global.t('messages.failedToPublishPackages');
            UserNotifications.error({ message: `${msg}` });
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      packages.forEach((pack) => {
        pack.state = Status.Retired;
        API.instance.putPackageState(
          pack,
          () => {
            successfullApiCalls += 1;
            evalCompletion();
          },
          () => {
            failedApiCalls += 1;
            evalCompletion();
          },
        );
      });
    });
  },

  savePackage({ commit }, { pack, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      const successCallback = (data) => {
        commit(types.SAVE_PACKAGE, pack, data);
        resolve(data);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      };

      const errorCallback = (error) => {
        const msg = i18n.global.t('messages.failedToSavePackage');
        ErrorHandler.showError(msg, error);
        reject(error);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      };

      if (pack.id === -1) {
        API.instance.postPackage(pack, successCallback, errorCallback);
      } else {
        API.instance.putPackage(pack, successCallback, errorCallback);
      }
    });
  },

  convertDealToLineitems({ commit }, pack) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      const successCallback = (data) => {
        commit(lineitemTypes.ADD_LINEITEM, data);
        resolve(data);
        GlobalEventBus.$emit(GlobalEvents.loaded);
        GlobalEventBus.$emit(GlobalEvents.pendingApprovalsChanged.default);
      };

      const errorCallback = (error) => {
        const msg = i18n.global.t('messages.failedToSavePackage');
        ErrorHandler.showError(msg, error);
        reject(error);
        GlobalEventBus.$emit(GlobalEvents.loaded);
      };
      API.instance.postConvertPackageToLineitems(pack, successCallback, errorCallback);
    });
  },

  getPackageCustomFields: createApiAction(API.instance.getPackageCustomFields),

  setPackageSummaryDateRange({ commit }, dateRange) {
    commit(types.UPDATE_PACKAGE_SUMMARY_DATE_RANGE, dateRange ?? { start: null, end: null });
  },
};

export default actions;
